import React, { FC, useCallback, useMemo } from 'react';
import { useOrderSummary } from '@/api/Order';
import { CustomColumns, CustomTable } from '@/ui/components';
import { useDeleteBillOfLading } from '@/api/BillOfLading';
import { useAppCustomers } from '@/hooks';
import { OrderGridDetailSection } from '../OrderGridDetailSection';
import { FormattedOrderSummaryData } from '../../types';
import { formatOrderSummary } from '../../utils';

export const OrderDataGrid: FC = () => {
  const { hasAdminAccess } = useAppCustomers();
  const { data, isFetching, refetch } = useOrderSummary();
  const { mutateAsync: deleteMutateAsync } = useDeleteBillOfLading();

  const dataSource = useMemo(
    () => formatOrderSummary(data?.entities || []),
    [data?.entities],
  );

  const handleDeleteBillOfLading = useCallback(
    async (movementId: number) => {
      await deleteMutateAsync(movementId);
      refetch();
    },
    [deleteMutateAsync, refetch],
  );

  const columns: CustomColumns<FormattedOrderSummaryData> = useMemo(
    () => [
      { dataIndex: 'customerName', title: 'Customer Name' },
      { dataIndex: 'consigneeCode', title: 'Consignee Code' },
      { dataIndex: 'country', title: 'Country' },
    ],
    [],
  );

  const masterDetailRender = useCallback(
    (value: FormattedOrderSummaryData) => (
      <OrderGridDetailSection
        hasAdminAccess={hasAdminAccess}
        dataSource={value.items}
        deleteBillOfLading={handleDeleteBillOfLading}
      />
    ),
    [handleDeleteBillOfLading, hasAdminAccess],
  );

  return (
    <CustomTable
      dataSource={dataSource}
      columns={columns}
      rowKey="companyId"
      loading={isFetching}
      expandable={{
        expandedRowRender: masterDetailRender,
      }}
    />
  );
};
